import React, { useContext, useMemo, useState } from 'react';
import { PlanningContext } from '../context';
import { Body2, GridItemRow, ModalActions, ModalContent, ModalTitle, PrimaryButton, SecondaryButton } from '../blocks';
import { MODALS } from '../constants';
import { useMutation } from '@apollo/client';
import { UPDATE_ROUTE_STOPS } from '../graphql/mutations';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { cloneDeep } from 'lodash';
import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { DISPATCH_PLAN_SERIALIZATION_KEY } from '@/graphql/serializingLink';
import { useClientUser } from '@/hooks';
import { PLAN_PAGE_REMOVE_WITHOUT_UNLOCK } from '@/constants/featureFlags';
import routeStatusOptions from '@/constants/routeStatusOptions';
import { buildDeliveryUnassignSet, collectMappingIdsForStop } from './removeHelpers';
import { patchPlanningDataOnUnassign } from '../graphql/planningBuckets';
import { Switch } from '@material-ui/core';
import styled from '@emotion/styled';

const ToggleContainer = styled(GridItemRow)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    margin: 12px 0;
`;

const ToggleLabel = styled.span`
    font-size: 14px;
    font-weight: 500;
`;

const StopRemoveModal = () => {
    const {
        state,
        actionableRoute,
        setActionableRoute,
        actionableStop,
        setActionableStop,
        modalOpen,
        setModalOpen,
        setError,
        callbacks,
        loading: contextLoading,
    } = useContext(PlanningContext);
    const { circles } = useClientUser();
    const [sendBackToUnscheduled, setSendBackToUnscheduled] = useState(false);

    const stopOrders = useMemo(() => {
        return (actionableRoute?.orders || [])
            .filter((mapping) => actionableStop?.orders?.includes(mapping.order_id))
            .map((mapping) => mapping.order);
    }, [actionableRoute, actionableStop]);

    const [updateStops, { loading }] = useMutation(UPDATE_ROUTE_STOPS, {
        // Stop/mapping lists self-heal from update_routes_by_pk's returning
        // snapshot. patchPlanningDataOnUnassign evicts the removed stops,
        // filters each removed order's slim routes list, and re-adds eligible
        // orders to the pickups/dropoffs buckets when the cache holds
        // fragment-complete rows; when it cannot prove completeness it flags
        // needsRefetch and onCompleted falls back to the whole-board refetch.
        context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
    });

    const handleModalClose = () => {
        setModalOpen(null);
        setActionableRoute(null);
        setActionableStop(null);
        setSendBackToUnscheduled(false);
    };

    const handleRemoveStop = async () => {
        let newStops = cloneDeep(actionableRoute?.stopsByRouteId || []);
        for (const order of stopOrders || []) {
            newStops = FTLStopHelpers.removeStop(order, { ...actionableRoute, stopsByRouteId: newStops });
        }

        const deliveries = actionableRoute.orders
            .filter(({ type, order_id }) => type !== 'PICKUP' && actionableStop?.orders?.includes(order_id))
            .map(({ order }) => order);

        // Stop removal is an explicit `delete_order_route_mappings` over every
        // mapping whose order is part of the stop; no wh_event is emitted.
        // See ./removeHelpers.js.

        const pickups = actionableRoute.orders
            .filter(({ type, order_id }) => type === 'PICKUP' && actionableStop?.orders?.includes(order_id))
            .map(({ order }) => order);

        const mappingIdsToDelete = collectMappingIdsForStop(actionableRoute, actionableStop);

        let additionalRouteUpdates = {};
        if (
            actionableRoute?.status !== routeStatusOptions.PLANNING.value &&
            circles?.[PLAN_PAGE_REMOVE_WITHOUT_UNLOCK]
        ) {
            additionalRouteUpdates = {
                ...additionalRouteUpdates,
                status: 'planning',
            };
        }

        const orderUpdates = [];

        // Handle deliveries
        if (deliveries && deliveries.length > 0) {
            orderUpdates.push(
                ...deliveries.map((order) => ({
                    where: { order_id: { _eq: order.order_id } },
                    _set: {
                        ...buildDeliveryUnassignSet(order),
                        // Send back to unscheduled if toggled
                        ...(sendBackToUnscheduled
                            ? {
                                  delivery_date: null,
                              }
                            : {}),
                    },
                }))
            );
        }

        // Handle pickups
        if (pickups && pickups.length > 0) {
            orderUpdates.push(
                ...pickups.map((order) => ({
                    where: { order_id: { _eq: order.order_id } },
                    _set: {
                        order_status: !!order.carrier_id ? 'claimed' : 'pending',
                        // Mirrors partnerAssignmentUpdates' unassign branch: releasing the partner just
                        // clears contractor_id, carrier_id is never touched.
                        ...(order.contractor_id ? { contractor_id: null } : {}),
                        // Send back to unscheduled if toggled
                        ...(sendBackToUnscheduled
                            ? {
                                  pickup_date: null,
                              }
                            : {}),
                    },
                }))
            );
        }

        let needsRefetch = false;

        updateStops({
            variables: {
                route_id: actionableRoute?.route_id,
                route_update: {
                    need_to_optimize: true,
                    ...additionalRouteUpdates,
                },
                events: [],
                mapping_ids_delete: mappingIdsToDelete,
                order_updates: orderUpdates,
                ...FTLStopHelpers.gqlStopUpdates(newStops, actionableRoute),
            },
            update: (cache, { data }) => {
                needsRefetch = patchPlanningDataOnUnassign(cache, { data, variables: state?.variables });
            },
            onError: (error) => {
                setError(
                    error,
                    `Failed to remove order(s) ${
                        stopOrders?.length ? stopOrders.map((o) => o?.order_number).join(', ') : '(orders not loaded)'
                    } from route ${actionableRoute?.route_number}`
                );
            },
            onCompleted: () => {
                handleModalClose();
                if (needsRefetch) {
                    callbacks.refetch();
                }
            },
        });
    };

    return (
        <NavResponsiveModal open={modalOpen === MODALS.ROUTE_REMOVE_STOP} onClose={handleModalClose}>
            <ModalTitle>{`Remove order(s) ${stopOrders.map((o) => o.order_number).join(', ')} from route ${
                actionableRoute?.route_number
            }`}</ModalTitle>
            <ModalContent>
                <GridItemRow>
                    <Body2>
                        By removing this stop, the timeframes of the stops after this will be effected. Confirm to
                        recalculate these timeframes.
                    </Body2>
                </GridItemRow>
                <ToggleContainer>
                    <ToggleLabel>Send back to Unscheduled</ToggleLabel>
                    <Switch
                        checked={sendBackToUnscheduled}
                        onChange={(e) => setSendBackToUnscheduled(e.target.checked)}
                        color="primary"
                        size="small"
                    />
                </ToggleContainer>
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={loading} onClick={handleModalClose}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={loading || contextLoading} onClick={handleRemoveStop}>
                    Confirm
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default StopRemoveModal;
